import { useLayoutEffect } from "react";
import {
  CHUNK_FALLBACK_RELOAD_MARK,
  CHUNK_RELOAD_KEY,
  CHUNK_RETRY_URL_PARAM,
  isChunkLoadError,
} from "@utils/chunkReload.ts";

export interface ChunkErrorFallbackProps {
  error: unknown;
}

/**
 * One-shot auto-reload for stale chunk URLs after deploy, then a manual reload CTA.
 * `error` is a prop (not `useRouteError()`) so the same UI works from any route errorElement.
 *
 * Flow: first chunk error in a session → set key + reload once → if still broken, show
 * “Update Available” and let the user retry (with cache-bust query param on the button).
 */
export const ChunkErrorFallback = ({ error }: ChunkErrorFallbackProps) => {
  // useLayoutEffect: run before paint so we reload immediately instead of flashing content.
  useLayoutEffect(() => {
    if (!isChunkLoadError(error)) return;

    const hasReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!hasReloaded) {
      // Mark first so index.tsx’s deferred vite:preloadError handler does not treat “key set” as exhausted.
      sessionStorage.setItem(CHUNK_FALLBACK_RELOAD_MARK, "1");
      sessionStorage.setItem(CHUNK_RELOAD_KEY, "true");
      window.location.reload();
    }
  }, [error]);

  if (!isChunkLoadError(error)) {
    return null;
  }

  const hasReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY);
  // After the automatic retry, key is set: either we are about to unload or the retry failed and we show the CTA.
  if (hasReloaded) {
    return (
      <div className="h-full p-4">
        <h1 className="mb-4 text-xl">Update Available</h1>
        <p className="mb-4">
          A new version of the application is available. Please reload to get the
          latest version.
        </p>
        <button
          type="button"
          onClick={() => {
            // Reset retry state so useLayoutEffect may run one automatic reload again on the next load.
            sessionStorage.removeItem(CHUNK_RELOAD_KEY);
            sessionStorage.removeItem(CHUNK_FALLBACK_RELOAD_MARK);
            const url = new URL(window.location.href);
            // New URL often bypasses cached shell HTML / SW; stripChunkRetryUrlParam() cleans the bar on boot.
            url.searchParams.set(CHUNK_RETRY_URL_PARAM, String(Date.now()));
            window.location.replace(url.toString());
          }}
          className="rounded bg-primary px-4 py-2 text-white hover:bg-primary/90"
        >
          Reload Page
        </button>
      </div>
    );
  }
  // First error in session: key still empty; effect above will set key and reload (nothing to show yet).
  return null;
};
